import { useEffect, useMemo, useRef, useState } from 'react'
import useFocusTrap from '../ds/hooks/useFocusTrap.js'
import { buildIndex, search, moveActive } from '../lib/searchIndex.js'

// Global ⌘K palette: fuzzy-search screens + records and navigate. Opening is
// owned by Taskboard (global keydown); this component owns query, results, and
// in-palette keyboard nav. Renders nothing when closed. Reuses the design
// system's overlay primitives (cn-modal-overlay + useFocusTrap) rather than the
// Modal component, since a palette has no title bar or close button.
export default function CommandPalette({
  open, onClose, onNavigate,
  contacts = [], companies = [], deals = [], engagements = [], projects = [],
}) {
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const paletteRef = useRef(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useFocusTrap(paletteRef, open)

  const index = useMemo(
    () => buildIndex({ contacts, companies, deals, engagements, projects }),
    [contacts, companies, deals, engagements, projects],
  )
  const groups = useMemo(() => search(index, query, { perGroupLimit: 6 }), [index, query])
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups])

  // Fresh palette on each open; reset active row whenever results change.
  useEffect(() => { if (open) { setQuery(''); setActive(0) } }, [open])
  useEffect(() => { setActive(0) }, [query])

  // Keep the highlighted row visible as the user arrows through results.
  useEffect(() => {
    if (!open || !flat.length || typeof document === 'undefined') return
    const idx = Math.min(active, flat.length - 1)
    const el = document.getElementById(`cmdk-opt-${idx}`)
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
  }, [active, open, flat.length])

  if (!open) return null

  const activeIdx = flat.length ? Math.min(active, flat.length - 1) : 0

  const activate = (item) => { if (!item) return; onNavigate(item.route); onCloseRef.current() }

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(moveActive(flat.length, activeIdx, 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(moveActive(flat.length, activeIdx, -1)) }
    else if (e.key === 'Enter') { e.preventDefault(); activate(flat[activeIdx]) }
    else if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current() }
  }

  // Running offset so each option gets a stable flat index for aria/active state.
  let offset = 0

  return (
    <div
      className="cn-modal-overlay cn-cmdk-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCloseRef.current() }}
    >
      <div ref={paletteRef} className="cn-cmdk" role="dialog" aria-modal="true" aria-label="Command palette">
        <input
          className="cn-cmdk-input"
          role="combobox"
          aria-label="Search"
          aria-expanded="true"
          aria-controls="cmdk-listbox"
          aria-activedescendant={flat.length ? `cmdk-opt-${activeIdx}` : undefined}
          aria-autocomplete="list"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search screens, people, companies, deals…"
        />
        <div id="cmdk-listbox" role="listbox" className="cn-cmdk-list">
          {flat.length === 0 && <div className="cn-cmdk-empty">No matches</div>}
          {groups.map((g) => {
            const start = offset
            offset += g.items.length
            return (
              <div key={g.group} role="group" aria-label={g.label}>
                <div className="cn-cmdk-group">{g.label}</div>
                {g.items.map((item, i) => {
                  const idx = start + i
                  return (
                    <div
                      key={item.id}
                      id={`cmdk-opt-${idx}`}
                      role="option"
                      aria-selected={idx === activeIdx}
                      className="cn-menu-item cn-cmdk-opt"
                      data-active={idx === activeIdx ? 'true' : 'false'}
                      onMouseEnter={() => setActive(idx)}
                      onMouseDown={(e) => { e.preventDefault(); activate(item) }}
                    >
                      <span className="cn-cmdk-title">{item.title}</span>
                      {item.subtitle && <span className="cn-cmdk-sub">{item.subtitle}</span>}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
